<template>
  <EChartCard
    :title="$t('outcomeDistribution')"
    :subtitle="$t('recordsByStatus')"
    :buildOption="buildOption"
    :height="360"
    @chart-click="handleChartClick"
  />
</template>

<script setup>
import { ref } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import EChartCard from '@/components/charts/base/EChartCard.vue';
import { axisColor, DIM_OPACITY } from '@/utils/chartTheme';
import { statusColors } from '@/utils/vizPalette';
import { useSelectableFilter } from '@/composables/useSelectableFilter';
import { sqlLiteral } from '@/utils/assetFields';

const store = useStore();
const { t } = useI18n();

const chartName = 'outcomeChart';
const { isSelected, hasSelection, sync, toggle } = useSelectableFilter(chartName);

// The inspection / asset outcome IS the item status (Passed, Remedial,
// Replace, Monitor ...). Values and colours come from the customer's own
// status templates - nothing here is hard-coded.
const query = `
  SELECT
    st.Title AS title,
    st.Color AS color,
    MIN(st.Sort) AS sort,
    COUNT(*) AS items,
    COUNT(DISTINCT i.assetKey) AS assets
  FROM filteredItems i
  JOIN status_templates st ON st.Id = i.statusId
  GROUP BY st.Title, st.Color
  ORDER BY sort
`;

const bars = ref([]);

const buildOption = async () => {
  sync();
  const conn = store.getters.getDBConnection;
  if (!conn) return null;

  const result = await conn.query(query);
  // Ascending, so the largest outcome lands at the top of a category yAxis.
  const rows = result.toArray()
    .map((row) => {
      const json = row.toJSON();
      return {
        title: json.title,
        color: json.color || statusColors.neutral,
        items: Number(json.items) || 0,
        assets: Number(json.assets) || 0,
      };
    })
    .sort((a, b) => a.items - b.items);
  if (rows.length === 0) return null;

  bars.value = rows;
  const total = rows.reduce((sum, row) => sum + row.items, 0);

  const fontColor = store.getters.chartFontColor;
  const gridColor = axisColor(store.state.theme);

  return {
    tooltip: {
      trigger: 'item',
      confine: true,
      formatter: (params) => {
        const row = rows[params.dataIndex];
        const share = total > 0 ? Math.round((row.items / total) * 100) : 0;
        return `<strong>${row.title}</strong><br/>${row.items} ${t('items')} (${share}%)<br/>${row.assets} ${t('assets')}`;
      },
    },
    grid: { left: 8, right: 64, top: 8, bottom: 4, containLabel: true },
    xAxis: {
      type: 'value',
      minInterval: 1,
      axisLabel: { color: fontColor },
      splitLine: { lineStyle: { color: gridColor } },
    },
    yAxis: {
      type: 'category',
      data: rows.map((row) => row.title),
      axisLine: { lineStyle: { color: gridColor } },
      axisTick: { show: false },
      axisLabel: { color: fontColor },
    },
    series: [
      {
        type: 'bar',
        barMaxWidth: 22,
        label: {
          show: true,
          position: 'right',
          color: fontColor,
          formatter: (params) => {
            const row = rows[params.dataIndex];
            const share = total > 0 ? Math.round((row.items / total) * 100) : 0;
            return `${row.items} (${share}%)`;
          },
        },
        data: rows.map((row) => ({
          value: row.items,
          itemStyle: {
            color: row.color,
            borderRadius: [0, 4, 4, 0],
            opacity: hasSelection.value && !isSelected(row.title) ? DIM_OPACITY : 1,
          },
        })),
      },
    ],
  };
};

const handleChartClick = async (params) => {
  const row = bars.value[params?.dataIndex];
  if (!row) return;

  // Any asset with at least one record in this status.
  await toggle(
    row.title,
    `assetKey IN (
      SELECT ai.assetKey FROM asset_items ai
      JOIN status_templates s ON s.Id = ai.statusId
      WHERE s.Title = ${sqlLiteral(row.title)}
    )`,
    `${t('outcome')}: ${row.title}`
  );
};
</script>
